import { useState, useCallback } from 'react'
import { sendCommand } from '../lib/extension'

export interface PendingInvitation {
  invitationId: string
  profileUrn: string
  memberId: string
  vanityName: string
  firstName: string
  lastName: string
  sentTimeLabel: string
  sentDaysAgo: number
  sentAt: number | null
  exactTimestamp: boolean
  photoUrl: string | null
}

export interface WithdrawnInvitation {
  memberId: string
  profileUrn: string
  vanityName: string
  firstName: string
  lastName: string
  photoUrl: string | null
  withdrawnAt: number
  originalSentLabel: string
}

const CACHE_KEY = 'slash-pending-invitations'
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes
const WITHDRAWN_KEY = 'slash-withdrawn-invitations'

function _readCache(): PendingInvitation[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const { invitations, time } = JSON.parse(raw)
    if (Date.now() - time > CACHE_TTL) return null
    return invitations
  } catch {
    return null
  }
}

function _writeCache(invitations: PendingInvitation[]) {
  localStorage.setItem(CACHE_KEY, JSON.stringify({ invitations, time: Date.now() }))
}

function _readWithdrawn(): WithdrawnInvitation[] {
  try {
    return JSON.parse(localStorage.getItem(WITHDRAWN_KEY) || '[]')
  } catch { return [] }
}

function _writeWithdrawn(list: WithdrawnInvitation[]) {
  localStorage.setItem(WITHDRAWN_KEY, JSON.stringify(list))
}

export function usePendingInvitations() {
  const [invitations, setInvitations] = useState<PendingInvitation[]>(() => _readCache() || [])
  const [withdrawn, setWithdrawn] = useState<WithdrawnInvitation[]>(() => _readWithdrawn())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchAll = useCallback(async (forceRefresh = false) => {
    const cached = _readCache()
    if (!forceRefresh && cached) {
      setInvitations(cached)
      return
    }

    setLoading(true)
    setError(null)
    // Completeness tracking (POST_FIX_ASTAR_AUDIT_HANDOFF finding 1):
    // the CRM sent-list diff must only run after a successful end-to-
    // end pagination. Any per-page failure means the list is partial
    // and we bail out of the diff call + preserve the cache. Setting
    // walkComplete=true happens only on the natural loop-exit path.
    let walkComplete = false
    let all: PendingInvitation[] = []
    try {
      let startIndex = 0
      const MAX_PAGES = 100 // safety limit to prevent infinite pagination
      for (let page = 0; page < MAX_PAGES; page++) {
        const resp = await sendCommand<{ invitations: PendingInvitation[] }>(
          'FETCH_SENT_INVITATIONS', { startIndex }
        )
        // The extension now throws on HTTP / parse failure rather than
        // returning []. A short page or empty page here therefore means
        // "walk complete" (reached the end), not "fetch errored" —
        // which used to be the same observation and fed mass false
        // 'accepted'/'expired' events into the CRM.
        const items = resp?.invitations ?? []
        all = [...all, ...items]
        if (items.length < 10) {
          // Reached the end of the list cleanly.
          walkComplete = true
          break
        }
        startIndex += items.length
      }
      // Normal loop-exit via MAX_PAGES counts as complete too — we
      // successfully fetched 100 pages of 10+ items each (1000+
      // invites) and gave up to protect against runaway pagination.
      // For practical users that's beyond realistic and almost
      // certainly means LinkedIn is returning a cycle; treat as
      // complete to avoid eternal partial-walk bailouts.
      if (!walkComplete) walkComplete = true

      _writeCache(all)
      setInvitations(all)

      // If a withdrawn profileUrn reappears in pending (user re-sent externally), remove from withdrawn
      const pendingUrns = new Set(all.map((i) => i.profileUrn))
      setWithdrawn((prev) => {
        const filtered = prev.filter((w) => !pendingUrns.has(w.profileUrn))
        if (filtered.length !== prev.length) _writeWithdrawn(filtered)
        return filtered
      })
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e)
      setError(message || 'Failed to fetch sent invitations')
      // DO NOT write the partial result to cache — the next render
      // (or another tab reading localStorage) would then see a
      // truncated list as "current", compounding the staleness. The
      // existing cache (if any) is left alone; the UI falls back to
      // it via `_readCache()` on the next fetchAll.
      // walkComplete stays false so the CRM sync is skipped below.
    } finally {
      setLoading(false)
    }

    // CRM sent-list diff — extension-side — ONLY on complete walk.
    // Partial / errored walks skip this entirely. Fire-and-forget;
    // CRM failures shouldn't block the UI.
    if (walkComplete) {
      sendCommand<{ ok: boolean; synced: boolean }>(
        'SYNC_SENT_INVITATION_EVENTS',
        { pending: all, isComplete: true },
      ).catch((err) => {
        // Extension-side diff errors are logged by the extension; we
        // just want to make sure the promise rejection doesn't become
        // an unhandled-promise warning.
        console.warn('[invites] CRM sent-list diff failed:', err?.message || err)
      })
    }
  }, [])

  const withdraw = useCallback(async (inv: PendingInvitation) => {
    try {
      const resp = await sendCommand<{ success: boolean }>('WITHDRAW_INVITATION', {
        invitationId: inv.invitationId,
        profileUrn: inv.profileUrn,
        vanityName: inv.vanityName,
        memberId: inv.memberId,
        firstName: inv.firstName,
        lastName: inv.lastName,
      })
      if (!resp?.success) {
        return { success: false, error: 'LinkedIn rejected the withdrawal' }
      }
      setInvitations((prev) => {
        const updated = prev.filter((i) => i.invitationId !== inv.invitationId)
        _writeCache(updated)
        return updated
      })
      setWithdrawn((prev) => {
        const entry: WithdrawnInvitation = {
          memberId: inv.memberId,
          profileUrn: inv.profileUrn,
          vanityName: inv.vanityName,
          firstName: inv.firstName,
          lastName: inv.lastName,
          photoUrl: inv.photoUrl,
          withdrawnAt: Date.now(),
          originalSentLabel: inv.sentTimeLabel,
        }
        const updated = [entry, ...prev].slice(0, 200)
        _writeWithdrawn(updated)
        return updated
      })
      return { success: true }
    } catch (e: any) {
      return { success: false, error: e.message }
    }
  }, [])

  const withdrawBulk = useCallback(
    async (targets: PendingInvitation[], onProgress?: (done: number, total: number) => void) => {
      const results: { inv: PendingInvitation; success: boolean }[] = []
      for (let i = 0; i < targets.length; i++) {
        if (i > 0) await new Promise((r) => setTimeout(r, 2000))
        const result = await withdraw(targets[i])
        results.push({ inv: targets[i], success: result.success })
        onProgress?.(i + 1, targets.length)
      }
      return results
    },
    [withdraw],
  )

  const dismissWithdrawn = useCallback((profileUrn: string) => {
    setWithdrawn((prev) => {
      const updated = prev.filter((w) => w.profileUrn !== profileUrn)
      _writeWithdrawn(updated)
      return updated
    })
  }, [])

  const clearWithdrawn = useCallback(() => {
    setWithdrawn([])
    _writeWithdrawn([])
  }, [])

  return { invitations, withdrawn, loading, error, fetchAll, withdraw, withdrawBulk, dismissWithdrawn, clearWithdrawn }
}
